/* motion.js — animation engine (vanilla, ~0 dependencies)
 *
 * Declarative API (add attributes in HTML, no extra JS needed):
 *   data-reveal[="up|down|left|right|fade|scale|blur|clip"]  entrance on scroll
 *   data-delay="120"                       extra delay in ms for that element
 *   data-stagger[="70"]                    children with [data-reveal] (or all children) get staggered delays
 *   data-split[="words|lines"]             headline split into words that rise in (never letters: Arabic-safe)
 *   data-count="1250" data-dec="1" data-from="0" data-suffix="+"   count-up when visible
 *   data-bar="78"                          width fills to 78% when visible (progress bars, rating bars)
 *   data-tilt[="8"]                        3D tilt following the pointer (max deg)
 *   data-magnetic[="0.3"]                  element is attracted to the pointer
 *   data-parallax="0.15"                   moves at a fraction of scroll speed (neg = opposite)
 *   data-marquee[="40"]                    duplicates children for a seamless loop (seconds per loop)
 *   data-countdown="2026-11-30T23:59:00+01:00"  fills [data-cd=d|h|m|s] children; adds .ended
 *   data-sequence="2200"                   children get .on one after another, looping (notification stacks)
 *   data-rail                              horizontal scroll-snap rail; adds a progress bar after it
 *   data-sticky-cta                        shown after the hero, hidden when #commande (or data-hide-at) is visible
 *   data-progress                          element whose --p (0..1) follows page scroll
 *   header[data-header]                    gets .scrolled after 20px, .hide when scrolling down (if data-autohide)
 *   nav a[data-spy="sectionId"]            active link + a sliding .nav-ind indicator inside the nav
 *   details (any)                          smooth open/close height animation
 *   [data-loop]                            CSS loops inside are paused while off-screen (.paused class)
 *
 * Everything respects prefers-reduced-motion and works without JS (reveal styles are scoped to .js).
 */
(function () {
  "use strict";
  var doc = document, root = doc.documentElement;
  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var finePointer = matchMedia("(hover: hover) and (pointer: fine)").matches;
  var rtl = (root.dir || doc.body.dir) === "rtl";
  var $ = function (s, r) { return (r || doc).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || doc).querySelectorAll(s)); };
  root.classList.add("js");
  if (reduce) root.classList.add("reduce");

  function onVisible(el, cb, opts) {
    if (!("IntersectionObserver" in window)) { cb(true); return; }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { cb(e.isIntersecting, e, io); });
    }, opts || { rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return io;
  }

  /* ---------- split headlines into words ---------- */
  $$("[data-split]").forEach(function (el) {
    var i = 0;
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var frag = doc.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(part)); return; }
            var w = doc.createElement("span"); w.className = "w";
            var inner = doc.createElement("span"); inner.textContent = part;
            inner.style.setProperty("--i", i++);
            w.appendChild(inner); frag.appendChild(w);
          });
          n.parentNode.replaceChild(frag, n);
        } else if (n.nodeType === 1 && !n.classList.contains("w")) {
          // keep styled words (em, strong, .grad) as a single animated unit
          if (n.children.length === 0 && n.textContent.trim().split(/\s+/).length === 1) {
            var w = doc.createElement("span"); w.className = "w";
            n.parentNode.insertBefore(w, n); w.appendChild(n);
            n.style.setProperty("--i", i++); n.classList.add("wi");
          } else walk(n);
        }
      });
    })(el);
    if (!el.hasAttribute("data-reveal")) el.setAttribute("data-reveal", "split");
  });

  /* ---------- stagger ---------- */
  $$("[data-stagger]").forEach(function (group) {
    var step = parseInt(group.getAttribute("data-stagger"), 10) || 70;
    var kids = $$(":scope > [data-reveal]", group);
    if (!kids.length) kids = Array.prototype.slice.call(group.children).map(function (k) {
      k.setAttribute("data-reveal", group.getAttribute("data-stagger-type") || "up"); return k;
    });
    kids.forEach(function (k, i) {
      var base = parseInt(k.getAttribute("data-delay"), 10) || 0;
      k.style.setProperty("--delay", base + i * step + "ms");
    });
  });

  /* ---------- reveal ---------- */
  $$("[data-reveal]").forEach(function (el) {
    var d = el.getAttribute("data-delay");
    if (d && !el.style.getPropertyValue("--delay")) el.style.setProperty("--delay", d + "ms");
    if (reduce) { el.classList.add("in"); return; }
    onVisible(el, function (v, e, io) { if (v) { el.classList.add("in"); io.unobserve(el); } });
  });
  // safety net: never leave content hidden (slow devices, print, anchors)
  setTimeout(function () { $$("[data-reveal]:not(.in)").forEach(function (el) {
    if (el.getBoundingClientRect().top < innerHeight) el.classList.add("in"); }); }, 2500);

  /* ---------- counters & bars ---------- */
  var locale = root.lang || "fr";
  function countUp(el) {
    var to = parseFloat(el.getAttribute("data-count")), dec = +el.getAttribute("data-dec") || 0;
    var from = el.hasAttribute("data-from") ? parseFloat(el.getAttribute("data-from")) : 0;
    var suf = el.getAttribute("data-suffix") || "", pre = el.getAttribute("data-prefix") || "";
    var dur = +el.getAttribute("data-duration") || 1600, t0 = null;
    var fmt = function (v) {
      return pre + v.toLocaleString(locale.indexOf("ar") === 0 ? "fr" : locale,
        { minimumFractionDigits: dec, maximumFractionDigits: dec }) + suf;
    };
    if (reduce) { el.textContent = fmt(to); return; }
    (function f(ts) {
      if (!t0) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur), e = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(from + (to - from) * e);
      if (p < 1) requestAnimationFrame(f);
    })(performance.now());
  }
  $$("[data-count]").forEach(function (el) {
    onVisible(el, function (v, e, io) { if (v) { countUp(el); io.unobserve(el); } });
  });
  $$("[data-bar]").forEach(function (el) {
    el.style.setProperty("--w", "0%");
    onVisible(el, function (v, e, io) {
      if (v) { el.style.setProperty("--w", el.getAttribute("data-bar") + "%"); io.unobserve(el); }
    });
  });

  /* ---------- pointer effects ---------- */
  if (finePointer && !reduce) {
    $$("[data-tilt]").forEach(function (el) {
      var max = parseFloat(el.getAttribute("data-tilt")) || 8, raf = 0;
      el.addEventListener("pointermove", function (e) {
        var r = el.getBoundingClientRect();
        var x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          el.style.transform = "perspective(900px) rotateX(" + (-y * max) + "deg) rotateY(" + (x * max) + "deg)";
          el.style.setProperty("--mx", (x + 0.5) * 100 + "%");
          el.style.setProperty("--my", (y + 0.5) * 100 + "%");
        });
      });
      el.addEventListener("pointerleave", function () { el.style.transform = ""; });
    });
    $$("[data-magnetic]").forEach(function (el) {
      var k = parseFloat(el.getAttribute("data-magnetic")) || 0.3;
      el.addEventListener("pointermove", function (e) {
        var r = el.getBoundingClientRect();
        el.style.transform = "translate(" + (e.clientX - r.left - r.width / 2) * k + "px," + (e.clientY - r.top - r.height / 2) * k + "px)";
      });
      el.addEventListener("pointerleave", function () { el.style.transform = ""; });
    });
  }

  /* ---------- marquee ---------- */
  $$("[data-marquee]").forEach(function (el) {
    var track = el.firstElementChild; if (!track) return;
    track.innerHTML += track.innerHTML; // seamless: animate -50%
    $$(":scope > *", track).slice(track.children.length / 2).forEach(function (c) { c.setAttribute("aria-hidden", "true"); });
    el.style.setProperty("--dur", (parseFloat(el.getAttribute("data-marquee")) || 40) + "s");
  });

  /* ---------- countdown (real deadline only) ---------- */
  $$("[data-countdown]").forEach(function (el) {
    var end = new Date(el.getAttribute("data-countdown")).getTime();
    if (isNaN(end)) return;
    var parts = { d: $("[data-cd=d]", el), h: $("[data-cd=h]", el), m: $("[data-cd=m]", el), s: $("[data-cd=s]", el) };
    var last = {};
    function set(k, v) {
      if (!parts[k]) return;
      v = String(v).padStart(2, "0");
      if (last[k] !== v) { parts[k].textContent = v; parts[k].classList.remove("tick"); void parts[k].offsetWidth; parts[k].classList.add("tick"); last[k] = v; }
    }
    (function tick() {
      var t = Math.max(0, end - Date.now()), s = Math.floor(t / 1000);
      if (!parts.d) { set("h", Math.floor(s / 3600)); } else { set("d", Math.floor(s / 86400)); set("h", Math.floor(s / 3600) % 24); }
      set("m", Math.floor(s / 60) % 60); set("s", s % 60);
      if (t <= 0) { el.classList.add("ended"); return; }
      setTimeout(tick, 1000 - (Date.now() % 1000));
    })();
  });

  /* ---------- sequences (notification stacks, chat bubbles) ---------- */
  $$("[data-sequence]").forEach(function (el) {
    var items = Array.prototype.slice.call(el.children), every = +el.getAttribute("data-sequence") || 2200;
    if (reduce) { items.forEach(function (i) { i.classList.add("on"); }); return; }
    var k = 0, visible = true;
    onVisible(el, function (v) { visible = v; }, { rootMargin: "0px" });
    function step() {
      if (!visible) return;
      var pos = k % (items.length + 2);
      if (pos < items.length) items[pos].classList.add("on");
      else if (pos === items.length + 1) items.forEach(function (i) { i.classList.remove("on"); });
      k++;
    }
    setTimeout(step, 500); setInterval(step, every);
  });

  /* ---------- pause CSS loops off-screen ---------- */
  $$("[data-loop]").forEach(function (el) {
    onVisible(el, function (v) { el.classList.toggle("paused", !v); }, { rootMargin: "100px" });
  });

  /* ---------- rails with progress ---------- */
  $$("[data-rail]").forEach(function (rail) {
    var bar = doc.createElement("div"); bar.className = "rail-prog"; bar.innerHTML = "<i></i>";
    rail.parentNode.insertBefore(bar, rail.nextSibling);
    function upd() {
      var max = rail.scrollWidth - rail.clientWidth;
      bar.hidden = max < 8;
      var p = max > 0 ? Math.abs(rail.scrollLeft) / max : 0;
      bar.style.setProperty("--p", p.toFixed(3));
      bar.style.setProperty("--size", Math.max(0.15, rail.clientWidth / rail.scrollWidth).toFixed(3));
    }
    rail.addEventListener("scroll", upd, { passive: true }); addEventListener("resize", upd); upd();
  });

  /* ---------- smooth <details> ---------- */
  $$("details").forEach(function (d) {
    var sum = $("summary", d), body = sum && sum.nextElementSibling;
    if (!sum || !body || reduce) return;
    sum.addEventListener("click", function (e) {
      e.preventDefault();
      if (d.open) {
        body.style.height = body.scrollHeight + "px";
        requestAnimationFrame(function () { body.style.height = "0px"; });
        d.classList.remove("opening");
        body.addEventListener("transitionend", function te() { d.open = false; body.style.height = ""; body.removeEventListener("transitionend", te); });
      } else {
        // optional accordion behaviour inside [data-accordion]
        var group = d.closest("[data-accordion]");
        if (group) $$("details[open]", group).forEach(function (o) { if (o !== d) $("summary", o).click(); });
        d.open = true; d.classList.add("opening");
        var h = body.scrollHeight; body.style.height = "0px";
        requestAnimationFrame(function () { body.style.height = h + "px"; });
        body.addEventListener("transitionend", function te() { body.style.height = ""; body.removeEventListener("transitionend", te); });
      }
    });
  });

  /* ---------- scroll-driven: header, progress, parallax, sticky CTA ---------- */
  var header = $("[data-header]"), progress = $$("[data-progress]"), para = $$("[data-parallax]");
  var sticky = $("[data-sticky-cta]"), hero = (sticky && sticky.getAttribute("data-after") && $(sticky.getAttribute("data-after"))) || $(".hero") || $("section");
  var hideAt = sticky ? $(sticky.getAttribute("data-hide-at") || "#commande") : null, hideAtVisible = false;
  if (hideAt) onVisible(hideAt, function (v) { hideAtVisible = v; doc.body.classList.toggle("at-form", v); ticking || requestAnimationFrame(frame); }, { rootMargin: "0px" });
  var lastY = scrollY, ticking = false;
  function frame() {
    ticking = false;
    var y = scrollY, h = root.scrollHeight - innerHeight;
    if (header) {
      header.classList.toggle("scrolled", y > 20);
      if (header.hasAttribute("data-autohide")) header.classList.toggle("hide", y > lastY && y > 300);
    }
    progress.forEach(function (p) { p.style.setProperty("--p", h > 0 ? (y / h).toFixed(4) : 0); });
    if (!reduce) para.forEach(function (el) {
      var r = el.getBoundingClientRect(); if (r.bottom < -200 || r.top > innerHeight + 200) return;
      var c = r.top + r.height / 2 - innerHeight / 2;
      el.style.translate = "0 " + (-c * parseFloat(el.getAttribute("data-parallax"))).toFixed(1) + "px";
    });
    if (sticky) {
      var past = hero ? y > hero.offsetTop + hero.offsetHeight - 120 : y > 500;
      sticky.classList.toggle("show", past && !hideAtVisible && !doc.body.classList.contains("menu-open"));
    }
    lastY = y;
  }
  addEventListener("scroll", function () { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }, { passive: true });
  addEventListener("resize", frame); frame();

  /* ---------- scroll-spy nav with sliding indicator ---------- */
  var spyLinks = $$("a[data-spy]");
  if (spyLinks.length) {
    var nav = spyLinks[0].parentElement.closest("nav") || spyLinks[0].parentElement;
    var ind = $(".nav-ind", nav);
    var moveInd = function (a) {
      spyLinks.forEach(function (l) { l.classList.toggle("on", l === a); });
      if (!ind) return;
      if (!a) { ind.style.opacity = 0; return; }
      var nr = nav.getBoundingClientRect(), r = a.getBoundingClientRect();
      ind.style.left = r.left - nr.left + "px"; ind.style.width = r.width + "px"; ind.style.opacity = 1;
    };
    var current = null;
    spyLinks.forEach(function (l) {
      var sec = doc.getElementById(l.getAttribute("data-spy")); if (!sec) return;
      onVisible(sec, function (v) { if (v) { current = l; moveInd(l); } else if (current === l) { current = null; moveInd(null); } },
        { rootMargin: "-45% 0px -50% 0px" });
      l.addEventListener("mouseenter", function () { moveInd(l); });
    });
    nav.addEventListener("mouseleave", function () { moveInd(current); });
  }

  /* ---------- mobile menu ---------- */
  var burger = $("[data-burger]"), menu = $("[data-menu]");
  if (burger && menu) {
    var setMenu = function (open) {
      doc.body.classList.toggle("menu-open", open);
      burger.setAttribute("aria-expanded", open ? "true" : "false");
    };
    burger.addEventListener("click", function () { setMenu(!doc.body.classList.contains("menu-open")); });
    $$("a", menu).forEach(function (a) { a.addEventListener("click", function () { setMenu(false); }); });
    addEventListener("keydown", function (e) { if (e.key === "Escape") setMenu(false); });
  }

  /* ---------- optional smooth scroll (Lenis loaded from CDN) ---------- */
  if (window.Lenis && !reduce) {
    var lenis = new window.Lenis({ lerp: 0.1, smoothWheel: true });
    (function raf(t) { lenis.raf(t); requestAnimationFrame(raf); })(performance.now());
    lenis.on("scroll", function () { if (!ticking) { ticking = true; requestAnimationFrame(frame); } });
    $$('a[href^="#"]').forEach(function (a) {
      a.addEventListener("click", function (e) {
        var t = a.getAttribute("href"); if (t.length < 2) return;
        var el = $(t); if (!el) return; e.preventDefault(); lenis.scrollTo(el, { offset: -80 });
      });
    });
    window.lenis = lenis;
  }

  /* ---------- page transitions for multi-page sites (fallback when View Transitions unsupported) ---------- */
  if (!("onpagereveal" in window) && !reduce && !(window.Shopify && Shopify.designMode)) {
    $$("a[href]").forEach(function (a) {
      var href = a.getAttribute("href");
      if (!href || href[0] === "#" || /^\/(cart|checkout|account)/.test(href) || a.target === "_blank" || /^(https?:|mailto:|tel:|wa\.me)/.test(href) || a.hasAttribute("download")) return;
      a.addEventListener("click", function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault(); doc.body.classList.add("leaving");
        setTimeout(function () { location.href = href; }, 280);
      });
    });
    addEventListener("pageshow", function () { doc.body.classList.remove("leaving"); });
  }

  window.Motion = { onVisible: onVisible, countUp: countUp, reduce: reduce, rtl: rtl };
})();
